type SequentialPollTask = (signal: AbortSignal) => Promise<unknown>;

interface StartSequentialPollOptions {
  leading?: boolean;
}

interface AxiosLikeError {
  code?: unknown;
  isAxiosError?: unknown;
  message?: unknown;
  name?: unknown;
  response?: {
    status?: number;
  };
}

const isCanceledError = (error: AxiosLikeError): boolean => {
  return error.code === 'ERR_CANCELED' || error.name === 'CanceledError';
};

const isAxiosLikeError = (error: unknown): error is AxiosLikeError => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  return ('isAxiosError' in error && error.isAxiosError === true) || ('name' in error && error.name === 'AxiosError');
};

export const isAbortedPollError = (error: unknown): boolean => {
  return isAxiosLikeError(error) && isCanceledError(error);
};

/** Axios without HTTP status — timeout / dropped connection. Same case as Sentry `[unknown]`. */
export const isRetryablePollError = (error: unknown): boolean => {
  if (!isAxiosLikeError(error) || isCanceledError(error)) {
    return false;
  }

  return error.response?.status == null;
};

/** One in-flight request at a time. Next request starts only after `intervalMs` from the previous finish. */
export const startSequentialPoll = (
  task: SequentialPollTask,
  intervalMs: number,
  options?: StartSequentialPollOptions,
): (() => void) => {
  const leading = options?.leading ?? true;
  let abortController = new AbortController();
  let isStopped = false;
  let isPaused = document.hidden;
  let waitTimeoutId: ReturnType<typeof setTimeout> | undefined;
  let resolveWait: (() => void) | undefined;
  let resolveVisibility: (() => void) | undefined;

  const clearIntervalWait = (): void => {
    if (waitTimeoutId !== undefined) {
      clearTimeout(waitTimeoutId);
      waitTimeoutId = undefined;
    }

    resolveWait?.();
    resolveWait = undefined;
  };

  const resumeVisibilityWait = (): void => {
    resolveVisibility?.();
    resolveVisibility = undefined;
  };

  const stopPolling = (): void => {
    if (isStopped) {
      return;
    }

    isStopped = true;
    isPaused = false;
    abortController.abort();
    clearIntervalWait();
    resumeVisibilityWait();
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };

  const handleVisibilityChange = (): void => {
    if (document.hidden) {
      if (isStopped || isPaused) {
        return;
      }

      isPaused = true;
      abortController.abort();
      clearIntervalWait();
      return;
    }

    if (isStopped || !isPaused) {
      return;
    }

    isPaused = false;
    abortController = new AbortController();
    resumeVisibilityWait();
  };

  const waitWhileHidden = async (): Promise<void> => {
    if (!isPaused || isStopped) {
      return;
    }

    await new Promise<void>(resolve => {
      resolveVisibility = resolve;
    });
  };

  const waitForInterval = async (): Promise<void> => {
    await new Promise<void>(resolve => {
      resolveWait = resolve;
      waitTimeoutId = setTimeout(() => {
        waitTimeoutId = undefined;
        resolveWait = undefined;
        resolve();
      }, intervalMs);
    });
  };

  const run = async (): Promise<void> => {
    if (!leading && !isStopped) {
      await waitForInterval();
    }

    while (true) {
      if (isStopped) {
        return;
      }

      await waitWhileHidden();

      if (isStopped) {
        return;
      }

      try {
        const result = await task(abortController.signal);

        if (isStopped || result === 'stop') {
          stopPolling();
          return;
        }
      } catch (error) {
        if (isStopped) {
          stopPolling();
          return;
        }

        if (isPaused && isAbortedPollError(error)) {
          continue;
        }

        if (!isRetryablePollError(error)) {
          stopPolling();
          return;
        }
      }

      if (isStopped) {
        return;
      }

      if (isPaused) {
        continue;
      }

      await waitForInterval();
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);
  void run();

  return () => {
    stopPolling();
  };
};
